import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import Hr from "components/atoms/Hr/Hr";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import CardNew from "components/molecules/Card/CardNew";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import MultiStopDetails from "components/organisms/QuoteRequestDetails/MultiStopDetails";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import QuoteShippingPartyDetails from "./QuoteShippingPartyDetails";

const QuoteSchedule = function ({ data, loadingData }) {
  const { id } = useParams();
  const isPortAddressDetailRenderingEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_PORT_ADDRESS_DETAIL_RENDERING);
  const { data: shipmentData } = useGetShipmentBasic(id, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const isOffPlatform = shipmentData?.shipment?.is_off_platform_shipment || false;

  const {
    pickup_address_name,
    pickup_address_type,
    pickup_airport,
    pickup_contact_name,
    pickup_contact_phone,
    pickup_contact_email,
    pickup_seaport,
    pickup_datetime,
    pickup_end_datetime,
    pickup_hours_start,
    pickup_hours_end,
    pickup_notes,
    stop1_address_name,
    stop1_address_type,
    stop1_airport,
    stop1_contact_name,
    stop1_contact_phone,
    stop1_contact_email,
    stop1_seaport,
    stop1_datetime,
    stop1_end_datetime,
    stop1_hours_start,
    stop1_hours_end,
    stop1_notes,
    address_v2,
    mileage,
    stops,
    multistop_instructions,
    consignee,
    notify_party,
  } = data?.schedule || {};

  const pickup_name = address_v2?.pickup_name ?? pickup_address_name;
  const pickup_street = address_v2?.pickup_street_address ?? data?.schedule?.pickup_street;
  const pickup_street_line_two = address_v2?.pickup_street_address_line_two ?? data?.schedule?.pickup_street_line_two;
  const pickup_city = address_v2?.pickup_city ?? data?.schedule?.pickup_city;
  const pickup_state = address_v2?.pickup_state ?? data?.schedule?.pickup_state;
  const pickup_country = address_v2?.pickup_country ?? data?.schedule?.pickup_country;
  const pickup_zipcode = address_v2?.pickup_zipcode ?? data?.schedule?.pickup_zipcode;

  const stop1_name = address_v2?.delivery_name ?? stop1_address_name;
  const stop1_street = address_v2?.delivery_street_address ?? data?.schedule?.stop1_street;
  const stop1_street_line_two = address_v2?.delivery_street_address_line_two ?? data?.schedule?.stop1_street_line_two;
  const stop1_city = address_v2?.delivery_city ?? data?.schedule?.stop1_city;
  const stop1_state = address_v2?.delivery_state ?? data?.schedule?.stop1_state;
  const stop1_country = address_v2?.delivery_country ?? data?.schedule?.stop1_country;
  const stop1_zipcode = address_v2?.delivery_zipcode ?? data?.schedule?.stop1_zipcode;

  const {
    pickup_location_reference_number,
    pickup_custom_address_reference,
    pickup_un_locode,
    pickup_is_port_address,
    delivery_location_reference_number,
    delivery_custom_address_reference,
    delivery_un_locode,
    delivery_is_port_address,
  } = address_v2 ?? {};
  const isPickupPortAddress = isPortAddressDetailRenderingEnabled && !!pickup_is_port_address;
  const isDeliveryPortAddress = isPortAddressDetailRenderingEnabled && !!delivery_is_port_address;

  const hasStops = stops?.length > 2;

  return (
    <CardNew
      className="mb-15 font-b3"
      isLoading={loadingData}
      title={<FormattedMessage id="quote-request-detail.schedule" defaultMessage="Schedule" />}
    >
      {!hasStops && (
        <>
          {pickup_name && !isPickupPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupCompany" defaultMessage="Pickup company" />
              </Col>
              <Col>{pickup_name}</Col>
            </Row>
          )}
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.pickupContact" defaultMessage="Pickup contact name" />
            </Col>
            <Col>{pickup_contact_name}</Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage
                id="quick-request-detail.schedule.pickupContactPhone"
                defaultMessage="Pickup contact phone"
              />
            </Col>
            <Col
            >
              <CopyToClipboard text={pickup_contact_phone} />
            </Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage
                id="quick-request-detail.schedule.pickupContactEmail"
                defaultMessage="Pickup contact email"
              />
            </Col>
            <Col
            >
              <CopyToClipboard text={pickup_contact_email} />
            </Col>
          </Row>
          {pickup_address_type === "Airport" && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupAirport" defaultMessage="Pickup airport" />
              </Col>
              <Col
              >
                <CopyToClipboard text={pickup_airport} />
              </Col>
            </Row>
          )}
          {pickup_address_type === "Port" && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupSeaport" defaultMessage="Pickup seaport" />
              </Col>
              <Col
              >
                <CopyToClipboard text={pickup_seaport} />
              </Col>
            </Row>
          )}
          {pickup_street && !isPickupPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupAddress" defaultMessage="Pickup address" />
              </Col>
              {isOffPlatform ? (
                <Col> - </Col>
              ) : (
                <Col
                >
                  <CopyToClipboard text={pickup_street} />
                  <CopyToClipboard text={pickup_street_line_two} />
                  <AddressLocodeAndCustomFields
                    un_locode={pickup_un_locode}
                    location_reference_number={pickup_location_reference_number}
                    custom_address_reference={pickup_custom_address_reference}
                  />
                </Col>
              )}
            </Row>
          )}
          {!isPickupPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage
                  id="quote-request-detail.pickupCityAndPostalCode"
                  defaultMessage="Pickup city and postal code"
                />
              </Col>
              <Col
              >
                <CopyToClipboard
                  text={isOffPlatform ? pickup_city : `${pickup_city}${pickup_zipcode && `, ${pickup_zipcode}`}`}
                />
              </Col>
            </Row>
          )}
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage
                id={isPickupPortAddress ? "quote-request-detail.pickupCountry" : "quote-request-detail.pickupStateAndCountry"}
                defaultMessage={isPickupPortAddress ? "Pickup country" : "Pickup state and country"}
              />
            </Col>
            <Col>
              {isPickupPortAddress
                ? pickup_country
                : isOffPlatform
                ? pickup_country
                : `${pickup_state ? `${pickup_state}, ` : ""}${pickup_country}`}
            </Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.pickupReadyDateTime" defaultMessage="Pickup ready date and time" />
            </Col>
            <Col>
              {isOffPlatform
                ? "-"
                : pickup_end_datetime
                ? `${pickup_datetime} till ${pickup_end_datetime}`
                : pickup_datetime}
            </Col>
          </Row>
          {pickup_hours_start && pickup_hours_end && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupHours" defaultMessage="Pickup hours" />
              </Col>
              <Col>
                {pickup_hours_start && pickup_hours_end ? (
                  <>
                    {moment(pickup_hours_start, "hh:mm:ss").format("HH:mm")} -{" "}
                    {moment(pickup_hours_end, "hh:mm:ss").format("HH:mm")}
                  </>
                ) : (
                  "-"
                )}
              </Col>
            </Row>
          )}
          {pickup_notes && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.pickupNotes" defaultMessage="Pickup notes" />
              </Col>
              <Col>{pickup_notes || "-"}</Col>
            </Row>
          )}

          <Hr />

          {stop1_name && !isDeliveryPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliveryCompany" defaultMessage="Delivery company" />
              </Col>
              <Col>{stop1_name}</Col>
            </Row>
          )}
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.deliveryContact" defaultMessage="Delivery contact name" />
            </Col>
            <Col>{stop1_contact_name}</Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.deliveryContactPhone" defaultMessage="Delivery contact phone" />
            </Col>
            <Col
            >
              <CopyToClipboard text={stop1_contact_phone} />
            </Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.deliveryContactEmail" defaultMessage="Delivery contact email" />
            </Col>
            <Col
            >
              <CopyToClipboard text={stop1_contact_email} />
            </Col>
          </Row>
          {stop1_address_type === "Airport" && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliveryAirport" defaultMessage="Delivery airport" />
              </Col>
              <Col
              >
                <CopyToClipboard text={stop1_airport} />
              </Col>
            </Row>
          )}
          {stop1_address_type === "Port" && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliverySeaport" defaultMessage="Delivery seaport" />
              </Col>
              <Col
              >
                <CopyToClipboard text={stop1_seaport} />
              </Col>
            </Row>
          )}
          {stop1_street && !isDeliveryPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliveryAddress" defaultMessage="Delivery address" />
              </Col>
              {isOffPlatform ? (
                <Col> - </Col>
              ) : (
                <Col
                >
                  <CopyToClipboard text={stop1_street} />
                  <CopyToClipboard text={stop1_street_line_two} />
                  <AddressLocodeAndCustomFields
                    un_locode={delivery_un_locode}
                    location_reference_number={delivery_location_reference_number}
                    custom_address_reference={delivery_custom_address_reference}
                  />
                </Col>
              )}
            </Row>
          )}
          {!isDeliveryPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage
                  id="quote-request-detail.deliveryCityAndPostalCode"
                  defaultMessage="Delivery city and postal code"
                />
              </Col>
              <Col
              >
                <CopyToClipboard
                  text={isOffPlatform ? stop1_city : `${stop1_city}${stop1_zipcode && `, ${stop1_zipcode}`}`}
                />
              </Col>
            </Row>
          )}
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage
                id={
                  isDeliveryPortAddress
                    ? "quote-request-detail.deliveryCountry"
                    : "quote-request-detail.deliveryStateAndCountry"
                }
                defaultMessage={isDeliveryPortAddress ? "Delivery country" : "Delivery state and country"}
              />
            </Col>
            <Col>
              {isDeliveryPortAddress
                ? stop1_country
                : isOffPlatform
                ? stop1_country
                : `${stop1_state ? `${stop1_state}, ` : ""}${stop1_country}`}
            </Col>
          </Row>
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage
                id="quote-request-detail.expectedDeliveryDateTime"
                defaultMessage="Expected delivery date and time"
              />
            </Col>
            <Col>
              {isOffPlatform ? (
                "-"
              ) : stop1_end_datetime === "ASAP" ? (
                <FormattedMessage id="quote-request-detail.asap" defaultMessage={stop1_end_datetime} />
              ) : (
                <>{stop1_end_datetime ? `${stop1_datetime} till ${stop1_end_datetime}` : stop1_datetime}</>
              )}
            </Col>
          </Row>

          {stop1_hours_start && stop1_hours_end && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliveryHours" defaultMessage="Delivery hours" />
              </Col>
              <Col>
                {stop1_hours_start && stop1_hours_end ? (
                  <>
                    {moment(stop1_hours_start, "hh:mm:ss").format("HH:mm")} -{" "}
                    {moment(stop1_hours_end, "hh:mm:ss").format("HH:mm")}
                  </>
                ) : (
                  "-"
                )}
              </Col>
            </Row>
          )}
          {stop1_notes && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.deliveryNotes" defaultMessage="Delivery notes" />
              </Col>
              <Col>{stop1_notes || "-"}</Col>
            </Row>
          )}
        </>
      )}
      {hasStops && <MultiStopDetails stops={stops} multistop_instructions={multistop_instructions} />}
      <QuoteShippingPartyDetails consignee={consignee} notifyParty={notify_party} />
      {mileage && (
        <>
          <Hr />
          <Row className="mb-2 ">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.mileage" defaultMessage="Mileage" />
            </Col>
            <Col>{mileage || "-"}</Col>
          </Row>
        </>
      )}
    </CardNew>
  );
};

QuoteSchedule.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
};

export default QuoteSchedule;
